import api from '@/lib/api/axios'
import { authHeaders } from '@/features/ai-chat/core/utils/authHeaders'
import type { ListParams, PaginatedResponse } from '@/lib/api/types'
import type { RunbookTask } from '@/types/entities'

export interface MasterRunbookTechnology {
    id?: number
    name: string
    category: string
}

export interface MasterRunbook {
    id: number
    description: string
    information: string
    iconsId?: number | null
    iconFilename?: string | null
    tasks?: RunbookTask[]
    // Shape of the runbook, aggregated by the list endpoint — how big it is, how long it
    // takes, and how much of it a person has to do by hand.
    taskCount?: number
    duration?: number | null
    manual?: number
    assisted?: number
    /** Tasks the restore agent performs. */
    agent?: number
    decisions?: number
    // AI generation inputs persisted with the runbook
    technologies?: MasterRunbookTechnology[]
    generationContext?: string
    // Auto-built detailed "RUNBOOK FOR" name, kept separate from the user-given name
    generatedName?: string
}

export const masterRunbooksApi = {
    list: (params?: ListParams) => {
        // Convert perPage to limit for backend compatibility
        const queryParams = params ? {
            ...params,
            limit: params.perPage || params.limit || 20,
            perPage: undefined,
        } : undefined
        // Remove undefined values
        if (queryParams) {
            Object.keys(queryParams).forEach(key => {
                if (queryParams[key as keyof typeof queryParams] === undefined) {
                    delete queryParams[key as keyof typeof queryParams]
                }
            })
        }
        return api.get<PaginatedResponse<MasterRunbook>>('/api/master-runbooks', { params: queryParams })
    },

    // Method recommendation from the AI-generator inputs captured on this runbook.
    // The task is sent as it is ON SCREEN. Writing a task and generating its script is one
    // act of authoring — the runbook should not have to be saved first just to find out
    // whether the task can be automated at all.
    generateTaskCode: (id: number, taskNo: number, draft?: { method?: string; task?: string; commands?: string }) =>
        api.post<{ success: boolean; data: GenerateCodeResult }>(`/api/master-runbooks/${id}/tasks/${taskNo}/generate-code`, draft ?? {}),

    /**
     * Streaming variant: the generate → review → fix loop is up to six model calls, which
     * is far too long to sit behind a spinner that says nothing. Phases arrive as they
     * happen; the result arrives once.
     */
    generateTaskCodeStreamed: async (
        id: number,
        taskNo: number,
        draft: { method?: string; task?: string; commands?: string },
        onPhase: (phase: string) => void,
    ): Promise<GenerateCodeResult> => {
        const res = await fetch(`/api/master-runbooks/${id}/tasks/${taskNo}/generate-code?stream=1`, {
            method: 'POST',
            headers: authHeaders({ 'Content-Type': 'application/json' }),
            body: JSON.stringify(draft),
        })
        if (!res.ok && !res.headers.get('content-type')?.includes('text/event-stream')) {
            const body = await res.json().catch(() => null)
            throw new Error(body?.message || `Generation failed (${res.status})`)
        }
        const reader = res.body?.getReader()
        if (!reader) throw new Error('No response stream')
        const decoder = new TextDecoder()
        let buffer = ''
        let result: GenerateCodeResult | null = null
        let failure: string | null = null
        for (;;) {
            const { done, value } = await reader.read()
            if (done) break
            buffer += decoder.decode(value, { stream: true })
            const lines = buffer.split('\n')
            buffer = lines.pop() || ''
            for (const line of lines) {
                if (!line.startsWith('data: ')) continue
                try {
                    const payload = JSON.parse(line.slice(6))
                    if (payload.status) onPhase(payload.status)
                    if (payload.error) failure = payload.error
                    if (payload.data) result = payload.data as GenerateCodeResult
                } catch { /* keepalive or partial frame */ }
            }
        }
        if (failure) throw new Error(failure)
        if (!result) throw new Error('Generation ended without returning a script')
        return result
    },
    /**
     * One script for several consecutive manual tasks, written on request and not stored
     * (POST /api/master-runbooks/:id/task-script). Sends the tasks as they are on screen.
     */
    taskScriptStreamed: async (
        id: number,
        request: { fromTask: number; toTask: number; hostKey: string; tasks: RunbookTask[] },
        onPhase: (phase: string) => void,
    ): Promise<TaskScriptResult> => {
        const res = await fetch(`/api/master-runbooks/${id}/task-script?stream=1`, {
            method: 'POST',
            headers: authHeaders({ 'Content-Type': 'application/json' }),
            body: JSON.stringify(request),
        })
        if (!res.ok && !res.headers.get('content-type')?.includes('text/event-stream')) {
            const body = await res.json().catch(() => null)
            throw new Error(body?.message || `The script could not be written (${res.status})`)
        }
        const reader = res.body?.getReader()
        if (!reader) throw new Error('No response stream')
        const decoder = new TextDecoder()
        let buffer = ''
        let result: TaskScriptResult | null = null
        let failure: string | null = null
        for (;;) {
            const { done, value } = await reader.read()
            if (done) break
            buffer += decoder.decode(value, { stream: true })
            const lines = buffer.split('\n')
            buffer = lines.pop() || ''
            for (const line of lines) {
                if (!line.startsWith('data: ')) continue
                try {
                    const p = JSON.parse(line.slice(6))
                    if (p.status) onPhase(p.status)
                    if (p.error) failure = p.error
                    if (p.data) result = p.data as TaskScriptResult
                } catch { /* keepalive or partial frame */ }
            }
        }
        if (failure) throw new Error(failure)
        if (!result) throw new Error('Writing the script ended without returning one')
        return result
    },

    listTaskCode: (id: number) =>
        api.get<{ success: boolean; data: { taskNos: number[] } }>(`/api/master-runbooks/${id}/code`),
    getTaskCode: (id: number, taskNo: number) =>
        api.get<{ success: boolean; data: { set: TaskCodeSet | null } }>(`/api/master-runbooks/${id}/tasks/${taskNo}/code`),

    getMethodSuggestion: (id: number) =>
        api.get<{ success: boolean; data: { suggestion: import('@/features/objects/utils/runbookAutomation').MethodSuggestion | null } }>(
            `/api/master-runbooks/${id}/method-suggestion`),

    get: (id: number) =>
        api.get<MasterRunbook>(`/api/master-runbooks/${id}`),

    create: (data: Omit<MasterRunbook, 'id'>) =>
        api.post<MasterRunbook>('/api/master-runbooks', data),

    update: (id: number, data: Partial<Omit<MasterRunbook, 'id'>>) =>
        api.put<MasterRunbook>(`/api/master-runbooks/${id}`, data),

    delete: (id: number) =>
        api.delete(`/api/master-runbooks/${id}`),
}

export interface TaskCodeFile {
    filePath: string
    content?: string
    description?: string | null
    sequence?: number | null
    size?: number
}
export interface TaskCodeSet {
    entryFile: string | null
    format: string | null
    description: string | null
    status: string
    verifyMethod: string | null
    verifiedAt: string | null
    version: number
    files: TaskCodeFile[]
}
export interface ReviewIssue {
    severity: 'critical' | 'major' | 'minor'
    file: string | null
    detail: string
}
export interface TaskScriptResult {
    fromTask: number
    toTask: number
    entryFile: string
    format: string
    invocation: string | null
    description: string
    files: { filePath: string; content: string; description?: string | null; sequence?: number }[]
    status: string
    review: { verdict: 'pass' | 'fail'; issues: { severity: string; file: string | null; detail: string }[] } | null
}

export interface GenerateCodeResult {
    generated: boolean
    method?: string
    entryFile: string | null
    format: string | null
    description: string | null
    fileCount: number
    files: TaskCodeFile[]
    status: string
    verifyMethod: string | null
    attempts?: number
    validation: { ran: boolean; valid: boolean; errorCount: number } | null
    review: { verdict: 'pass' | 'fail'; issues: ReviewIssue[] } | null
    warnings: { file: string; name: string; sample: string }[]
}
